// ABOUTME: Dialog for selecting sub-service orders (jobs) to create a job-level invoice
import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, CircularProgress, Alert, Checkbox,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    FormControlLabel, Switch, Chip, Paper,
    RadioGroup, Radio, FormControl, FormLabel,
} from '@mui/material';
import { toast } from 'react-toastify';
import invoicingService from '../../services/invoicing';

const JobSelectionDialog = ({ open, parentSoId, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [parentSo, setParentSo] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [selectedJobIds, setSelectedJobIds] = useState([]);

    // View preference state — 'price_qty' (Customer Tier) or 'component'
    const [viewType, setViewType] = useState('price_qty');

    const fetchJobs = useCallback(async () => {
        if (!parentSoId || !open) return;

        try {
            setLoading(true);
            setError(null);
            const response = await invoicingService.getInvoiceableJobs(parentSoId, showAll);
            setParentSo(response.data.parent_so);
            setJobs(response.data.jobs || []);
            setSelectedJobIds([]);
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to load sub-service orders';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [parentSoId, showAll, open]);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);


    const handleToggleJob = (jobId) => {
        setSelectedJobIds(prev =>
            prev.includes(jobId) ? prev.filter(id => id !== jobId) : [...prev, jobId]
        );
    };

    const handleSelectAll = () => {
        const selectableIds = jobs.filter(j => j.is_selectable).map(j => j.id);
        if (selectedJobIds.length === selectableIds.length) {
            setSelectedJobIds([]);
        } else {
            setSelectedJobIds(selectableIds);
        }
    };

    const handleSubmit = async () => {
        if (selectedJobIds.length === 0) {
            setError('Please select at least one sub-service order');
            return;
        }

        try {
            setSubmitting(true);
            setError(null);
            // For component mode, pass empty array — backend auto-derives from sub-SO component_name
            const response = await invoicingService.createFromJobs(
                parentSoId, selectedJobIds, viewType,
                null,
                [],
            );
            toast.success(`Invoice ${response.data.invoice_number} created from ${selectedJobIds.length} sub-service order(s)`);
            onSuccess(response.data);
            handleClose();
        } catch (err) {
            const msg = err.response?.data?.error || 'Failed to create invoice';
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        setSelectedJobIds([]);
        setError(null);
        setShowAll(false);
        setViewType('price_qty');
        onClose();
    };

    const selectableJobs = jobs.filter(j => j.is_selectable);
    const totalRate = jobs
        .filter(j => selectedJobIds.includes(j.id))
        .reduce((sum, j) => sum + (j.rate || 0), 0);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                Create Invoice from Sub-Service Orders
                {parentSo && (
                    <Typography variant="subtitle2" color="textSecondary">
                        Parent: {parentSo.name} {parentSo.company && `(${parentSo.company})`}
                    </Typography>
                )}
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* View type selector */}
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <FormLabel component="legend" sx={{ fontSize: 13, mb: 0.5 }}>Invoice view</FormLabel>
                    <RadioGroup
                        row
                        value={viewType}
                        onChange={(e) => setViewType(e.target.value)}
                    >
                        <FormControlLabel value="price_qty"  control={<Radio size="small" />} label="Customer Tier" />
                        <FormControlLabel value="component"  control={<Radio size="small" />} label="Component" />
                    </RadioGroup>
                </FormControl>

                {viewType === 'component' && (
                    <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
                        Components will be automatically picked from each selected sub-service order.
                        Make sure each sub-order has a component assigned.
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAll}
                                onChange={(e) => setShowAll(e.target.checked)}
                                disabled={loading}
                            />
                        }
                        label="Show all (including invoiced)"
                    />
                    <Typography variant="body2" color="textSecondary">
                        {selectableJobs.length} selectable, {selectedJobIds.length} selected
                    </Typography>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : jobs.length === 0 ? (
                    <Alert severity="info">
                        No sub-service orders found. {!showAll && 'Try enabling "Show all" to see invoiced orders.'}
                    </Alert>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selectedJobIds.length > 0 && selectedJobIds.length < selectableJobs.length}
                                            checked={selectableJobs.length > 0 && selectedJobIds.length === selectableJobs.length}
                                            onChange={handleSelectAll}
                                            disabled={selectableJobs.length === 0}
                                        />
                                    </TableCell>
                                    <TableCell>Sub-SO</TableCell>
                                    <TableCell>Service Type</TableCell>
                                    <TableCell>Component</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Rate</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {jobs.map((job) => {
                                    const isSelected = selectedJobIds.includes(job.id);
                                    return (
                                        <TableRow
                                            key={job.id}
                                            hover
                                            onClick={() => job.is_selectable && handleToggleJob(job.id)}
                                            sx={{
                                                cursor: job.is_selectable ? 'pointer' : 'default',
                                                opacity: job.is_selectable ? 1 : 0.6,
                                                bgcolor: isSelected ? 'action.selected' : 'inherit',
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={!job.is_selectable}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {job.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{job.service_type || '-'}</TableCell>
                                            <TableCell>{job.component || '-'}</TableCell>
                                            <TableCell>
                                                {job.is_invoiced ? (
                                                    <Chip label="Invoiced" size="small" color="default" />
                                                ) : job.is_completed ? (
                                                    <Chip label={job.status} size="small" color="success" />
                                                ) : (
                                                    <Chip label={job.status || 'Pending'} size="small" color="warning" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {job.rate ? `₹${job.rate.toLocaleString('en-IN')}` : '-'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {selectedJobIds.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="subtitle2">
                            Selected: {selectedJobIds.length} sub-service order(s)
                        </Typography>
                        <Typography variant="h6" color="primary">
                            Estimated Total: ₹{totalRate.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            (Taxes will be calculated based on rate card)
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || selectedJobIds.length === 0}
                >
                    {submitting ? <CircularProgress size={24} /> : `Create Invoice (${selectedJobIds.length})`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JobSelectionDialog;
